import { buildQs } from 'utils/queryString'
import { baseApi } from './baseApi'

export const organizationsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /** 取得組織客製化網站-品牌資料 */
        getPluginsCustomerWebsiteSetting: builder.query({
            query: (orgId) => ({
                url: `lessor/v1/organizations/${orgId}/plugins-customer-website/setting`,
                method: 'GET',
            }),
            providesTags: ['POST'],
        }),
        /** 同步組織客製化網站-品牌資料(檔案可選填) */
        updatePluginsCustomerWebsiteSettingSync: builder.mutation({
            query: ({ orgId, brand_logoFile, brand_bannerFile, ...data }) => {
                const formData = new FormData()
                Object.keys(data).map((key) => formData.append(key, data[key]))
                return {
                    url: `lessor/v1/organizations/${orgId}/plugins-customer-website/setting/sync`,
                    method: 'PATCH',
                    data: formData,
                }
            },
            providesTags: ['POST'],
        }),
        /** 取得一個組織專屬的可上傳儲存體檔案空間位置與上傳link
         * contentType: // image/jpeg,application/pdf
         */
        getBlobWriteSignedUrl: builder.query({
            query: (data) => {
                return {
                    url: `lessor/v1/organizations/${data.orgId}/blob-write-signed-url`,
                    method: 'POST',
                    data: {
                        content_type: data.contentType,
                    },
                }
            },
        }),
        /** 取得多個組織專屬儲存體檔案空間位置進行下載動作(檔案附件使用) */
        getMultiBlobReadSignedUrl: builder.query({
            query: ({ orgId, paths = [] }) => {
                return {
                    url: `lessor/v1/organizations/${orgId}/multi-blob-read-signed-url`,
                    method: 'POST',
                    data: { paths },
                }
            },
        }),
        /** 取得組織繳費設定 */
        getPaymentConfig: builder.query({
            query: ({ orgId }) => ({
                url: `lessor/v1/organizations/${orgId}/payment-config`,
                method: 'GET',
            }),
        }),
        /** 獲取所有屬於此組織的模板範本類型 */
        getTypesNewst: builder.query({
            keepUnusedDataFor: 0,
            query: (orgId) => ({
                url: `lessor/v1/organizations/${orgId}/types/newst`,
                method: 'GET',
            }),
        }),
        /* 獲取所有屬於此組織的最新模板範本說明頁面訊息 */
        // type: 合約範本類型。當有參考合約id時，此欄位無效。當無參考合約id時，此欄位必填
        getInfoNewst: builder.query({
            keepUnusedDataFor: 0,
            query: ({ orgId, type, houseId }) => {
                const searchParams = new URLSearchParams()
                if (houseId) {
                    searchParams.append('house_id', houseId)
                } else if (type) {
                    searchParams.append('type', type)
                }

                return {
                    url: `lessor/v1/organizations/${orgId}/info/newst?${searchParams.toString()}`,
                    method: 'GET',
                }
            },
        }),
        // 獲取組織提領紀錄相關之帳單
        getTakeoutHistoryBills: builder.query({
            keepUnusedDataFor: 0,
            query: ({ orgId, takeoutHistoryId, take, skip, type = [] }) => {
                const qs = buildQs({
                    take,
                    skip,
                    type,
                })

                return {
                    url: `lessor/v1/organizations/${orgId}/managements/bills/takeout-history/${takeoutHistoryId}/bills?${qs}`,
                    method: 'GET',
                }
            },
        }),

        // /lessor/v1/organizations/{organization_id}/members 組織內擁有使用者列表
        getOrganizationMembers: builder.query({
            query: ({ orgId }) => ({
                url: `lessor/v1/organizations/${orgId}/members`,
                method: 'GET',
            }),
        }),
        // /lessor/v1/organizations/members 多組織使用者列表
        getOrganizationMembersByOrgIds: builder.query({
            query: (orgIds) => {
                const qs = buildQs({
                    org_ids: orgIds,
                })
                return {
                    url: `lessor/v1/organizations/members?${qs}`,
                    method: 'GET',
                }
            },
        }),
        // /lessor/v1/organizations/{organization_id}/members 新增組織使用者
        addOrganizationMember: builder.mutation({
            query: ({ orgId, userShortId, role }) => ({
                url: `lessor/v1/organizations/${orgId}/members`,
                method: 'POST',
                data: {
                    user_short_id: userShortId,
                    role,
                },
            }),
        }),
        // /lessor/v1/organizations/{organization_id}/members/{user_id} 刪除組織使用者
        deleteOrganizationMember: builder.mutation({
            query: ({ orgId, userId }) => ({
                url: `lessor/v1/organizations/${orgId}/members/${userId}`,
                method: 'DELETE',
            }),
        }),
        // /lessor/v1/organizations/{organization_id}/members/{user_id} 修改組織使用者身份
        updateOrganizationMemberRole: builder.mutation({
            query: ({ orgId, userId, role }) => ({
                url: `lessor/v1/organizations/${orgId}/members/${userId}`,
                method: 'PATCH',
                data: {
                    role,
                },
            }),
        }),
        // /lessor/v1/organizations/{organization_id}/exit 離開特定組織
        exitOrganization: builder.mutation({
            query: ({ orgId }) => ({
                url: `lessor/v1/organizations/${orgId}/exit`,
                method: 'POST',
            }),
        }),
        // /lessor/v1/organizations/{organization_id}/clone Clone組織
        cloneOrganization: builder.mutation({
            query: ({ orgId, name, is_clone_all_members }) => ({
                url: `lessor/v1/organizations/${orgId}/clone`,
                method: 'POST',
                data: { name, is_clone_all_members },
            }),
        }),
        // /lessor/v1/organizations/juridical-person 創建法人組織
        createJuridicalOrganization: builder.mutation({
            query: ({
                business_registration,
                vat_number,
                tsn,
                juridical_person_name,
                juridical_person_tel,
                business_city,
                business_address,
                business_zip_code,
                bank_code,
                bank_branch,
                bank_account,
            }) => {
                const formData = new FormData()
                const data = {
                    business_registration,
                    vat_number,
                    tsn,
                    juridical_person_name,
                    juridical_person_tel,
                    business_city,
                    business_address,
                    business_zip_code,
                    bank_code,
                    bank_branch,
                    bank_account,
                }
                Object.keys(data).map((key) => formData.append(key, data[key]))
                return {
                    url: `lessor/v1/organizations/juridical-person`,
                    method: 'POST',
                    data: formData,
                }
            },
        }),
        createJuridicalOrganizationByProxy: builder.mutation({
            query: ({
                principal_identity_card, // 負責人身分證正面檔案
                principal_identity_card_back, // 負責人身分證反面檔案
                business_registration, // 營業登記証檔案(只接受PDF格式)
                vat_number, // 統一編號
                tsn, // 稅籍編號
                juridical_person_name, // 法人名稱（或公司名稱）- 並且會充當組織名稱
                juridical_person_tel, // 法人電話（或公司電話)
                principal_name, // 負責人身分證姓名
                principal_phone, // 負責人電話
                principal_identity_id, // 負責人身分證字號
                principal_identity_card_take_type, // 負責人身分證領發類別 (0: 沒有, 1: 初發, 2: 補發, 3: 換發)
                principal_identity_card_take_city, // 負責人身分證發證地點
                principal_identity_card_take_date, // 負責人身分證發證日期(七位數)(民國年月日)
                business_city, // 營業城市(僅接受台灣地區城市名稱)
                business_address, // 營業地址（只接受台灣地址)
                business_zip_code, // 郵遞區號(3+2 or 3+3)
                bank_code, // 銀行代碼+分行代碼 (參考/common/bank**** 相關API)
                bank_branch, // 銀行分行 (參考/common/bank**** 相關API)
                bank_account, // 銀行帳號
            }) => {
                const formData = new FormData()
                const data = {
                    principal_identity_card,
                    principal_identity_card_back,
                    business_registration,
                    vat_number,
                    tsn,
                    juridical_person_name,
                    juridical_person_tel,
                    principal_name,
                    principal_phone,
                    principal_identity_id,
                    principal_identity_card_take_type,
                    principal_identity_card_take_city,
                    principal_identity_card_take_date,
                    business_city,
                    business_address,
                    business_zip_code,
                    bank_code,
                    bank_branch,
                    bank_account,
                }
                Object.keys(data).map((key) => formData.append(key, data[key]))
                return {
                    url: `lessor/v1/organizations/juridical-person/by-proxy`,
                    method: 'POST',
                    data: formData,
                }
            },
        }),
        // 獲取組織專屬的 AI 偵測建物謄本 endpoint
        getBuildingTranscriptFileInfo: builder.mutation({
            query: ({ orgId, image, param1 }) => {
                const formData = new FormData()
                formData.append('image', image)
                formData.append('param1', param1)

                return {
                    url: `lessor/v1/organizations/${orgId}/image-detections/get-building-transcript-file-info`,
                    method: 'POST',
                    data: formData,
                    formData: true,
                }
            },
        }),
        // lessor/v1/organizations/{organization_id}/reset-juridical-person 重新創建法人組織（重新送審與補件) - 負責人為自己
        resetJuridicalOrganization: builder.mutation({
            query: ({
                org_id,
                business_registration, // 營業登記証檔案(只接受PDF格式)
                vat_number, // 統一編號
                tsn, // 稅籍編號
                juridical_person_name, // 法人名稱（或公司名稱）- 並且會充當組織名稱
                juridical_person_tel, // 法人電話（或公司電話)
                business_city, // 營業城市(僅接受台灣地區城市名稱)
                business_address, // 營業地址（只接受台灣地址)
                business_zip_code, // 郵遞區號(3+2 or 3+3)
                bank_code, // 銀行代碼+分行代碼 (參考/common/bank**** 相關API)
                bank_branch, // 銀行分行 (參考/common/bank**** 相關API)
                bank_account, // 銀行帳號
            }) => {
                const formData = new FormData()
                const data = {
                    business_registration,
                    vat_number,
                    tsn,
                    juridical_person_name,
                    juridical_person_tel,
                    business_city,
                    business_address,
                    business_zip_code,
                    bank_code,
                    bank_branch,
                    bank_account,
                }
                Object.keys(data).map((key) => formData.append(key, data[key]))
                return {
                    url: `lessor/v1/organizations/${org_id}/reset-juridical-person`,
                    method: 'POST',
                    data: formData,
                }
            },
        }),
        // lessor/v1/organizations/{organization_id}/reset-juridical-person/by-proxy 重新創建法人組織（重新送審與補件) - 自己非負責人
        resetJuridicalOrganizationByProxy: builder.mutation({
            query: ({
                org_id,
                business_registration, // 營業登記証檔案(只接受PDF格式)
                vat_number, // 統一編號
                tsn, // 稅籍編號
                juridical_person_name, // 法人名稱（或公司名稱）- 並且會充當組織名稱
                juridical_person_tel, // 法人電話（或公司電話)
                business_city, // 營業城市(僅接受台灣地區城市名稱)
                business_address, // 營業地址（只接受台灣地址)
                business_zip_code, // 郵遞區號(3+2 or 3+3)
                bank_code, // 銀行代碼+分行代碼 (參考/common/bank**** 相關API)
                bank_branch, // 銀行分行 (參考/common/bank**** 相關API)
                bank_account, // 銀行帳號
                principal_identity_card, // 負責人身分證正面檔案
                principal_identity_card_back, // 負責人身分證反面檔案
                principal_name, // 負責人身分證姓名
                principal_phone, // 負責人電話
                principal_identity_id, // 負責人身分證字號
                principal_identity_card_take_type, // 負責人身分證領發類別 (0: 沒有, 1: 初發, 2: 補發, 3: 換發)
                principal_identity_card_take_city, // 負責人身分證發證地點
                principal_identity_card_take_date, // 負責人身分證發證日期(七位數)(民國年月日)
            }) => {
                const formData = new FormData()
                const data = {
                    business_registration,
                    vat_number,
                    tsn,
                    juridical_person_name,
                    juridical_person_tel,
                    business_city,
                    business_address,
                    business_zip_code,
                    bank_code,
                    bank_branch,
                    bank_account,
                    principal_identity_card,
                    principal_identity_card_back,
                    principal_name,
                    principal_phone,
                    principal_identity_id,
                    principal_identity_card_take_type,
                    principal_identity_card_take_city,
                    principal_identity_card_take_date,
                }
                Object.keys(data).map((key) => formData.append(key, data[key]))
                return {
                    url: `lessor/v1/organizations/${org_id}/reset-juridical-person/by-proxy`,
                    method: 'POST',
                    data: formData,
                }
            },
        }),
    }),
})

export const {
    useGetPluginsCustomerWebsiteSettingQuery,
    useUpdatePluginsCustomerWebsiteSettingSyncMutation,
    useLazyGetBlobWriteSignedUrlQuery,
    useGetMultiBlobReadSignedUrlQuery,
    useGetPaymentConfigQuery,
    useGetTypesNewstQuery,
    useGetInfoNewstQuery,
    useGetTakeoutHistoryBillsQuery,
    useGetOrganizationMembersQuery,
    useGetOrganizationMembersByOrgIdsQuery,
    useAddOrganizationMemberMutation,
    useDeleteOrganizationMemberMutation,
    useUpdateOrganizationMemberRoleMutation,
    useExitOrganizationMutation,
    useCloneOrganizationMutation,
    useCreateJuridicalOrganizationMutation,
    useGetBuildingTranscriptFileInfoMutation,
    useCreateJuridicalOrganizationByProxyMutation,
    useResetJuridicalOrganizationByProxyMutation,
    useResetJuridicalOrganizationMutation,
} = organizationsApi
